/**
 * CMS string parsing for inline tokens, highlights and jargon tooltips.
 *
 * Three syntaxes Paul can use in any Payload textarea/text field:
 *
 *   {tokenName}            - replaced with site-settings or page values
 *   **word**               - punk highlight (default page colour)
 *   **word|green**         - punk highlight in a specific colour
 *   [[jargon:slug]]        - jargon popover (auto-label)
 *   [[jargon:slug|label]]  - jargon popover with custom label
 *
 * Tokens always available via buildContentTokens(settings, siteContent):
 *   {drivewayPrice}        - formatted driveway price (e.g. "$799")
 *   {drivewayFullPrice}    - formatted "was" price (e.g. "$1,200")
 *   {drivewayHeadline}     - toggle-aware headline, e.g. "Driveway Special
 *                            $799* (Usually $1,200)" or "Driveway From $799"
 *   {drivewayPriceClause}  - toggle-aware mid-sentence clause, e.g. "$799*
 *                            (usually $1,200)" or "from $799"
 *   {liabilityCover}       - "$10M"
 *   {abn}                  - business ABN
 *   {suburb}, {state}, {postcode}
 *   {machineMobilisationPerKm}, {siteEstablishmentFee}
 *   {homeBase}             - "Cockatoo" (service-area phrasing)
 *   {region}               - "Dandenong Ranges"
 *   {wider}                - "eastern Victoria"
 *
 * Pages that quote individual service catalogue items (e.g. the services
 * page's retaining wall, drainage and landscape feature cards) additionally
 * get one {price:<slug>} token per active catalogue row from
 * buildCataloguePriceTokens(), keyed by the same slug as the Pricing global's
 * Service catalogue tab. This is how a catalogue price edit reaches page copy
 * without hand-retyping the digits into every card that quotes it.
 */

import type { SiteSettings } from '../site-settings'
import { formatPrice } from '../utils'
import type { CatalogueItem } from './catalogue-pricing'
import type { SiteContent } from './defaults/site-content'

type TokenMap = Record<string, string>

export function applyTokens(input: string, tokens: TokenMap): string {
  if (!input) return input
  return Object.entries(tokens).reduce(
    (out, [key, value]) => out.replaceAll(`{${key}}`, value),
    input
  )
}

/**
 * Flattens highlight and jargon markup down to plain text for contexts that
 * cannot render JSX, e.g. <meta name="description">, JSON-LD, OG tags.
 *
 *   **word**           -> word
 *   **word|colour**    -> word
 *   [[jargon:slug]]    -> slug
 *   [[jargon:slug|x]]  -> x
 */
export function toPlainText(input: string): string {
  if (!input) return input
  const segments = parseInline(input)
  if (segments.length === 0) return input
  return segments
    .map((seg) => {
      if (seg.type === 'text') return seg.text
      if (seg.type === 'highlight') return seg.text
      if (seg.type === 'aside') return `(${seg.text})`
      return seg.label ?? seg.slug
    })
    .join('')
}

/**
 * Standard token map for content fields that reference site-wide values.
 * Pages spread the result and add their own page-specific tokens on top.
 */
export function buildContentTokens(settings: SiteSettings, siteContent: SiteContent): TokenMap {
  const drivewayPrice = formatPrice(settings.drivewayPrice)
  const drivewayFullPrice = formatPrice(settings.drivewayFullPrice)
  const drivewayHeadline = settings.drivewaySpecialActive
    ? `Driveway Special ${drivewayPrice}* (Usually ${drivewayFullPrice})`
    : `Driveway From ${drivewayPrice}`
  const drivewayPriceClause = settings.drivewaySpecialActive
    ? `${drivewayPrice}* (usually ${drivewayFullPrice})`
    : `from ${drivewayPrice}`
  return {
    drivewayPrice,
    drivewayFullPrice,
    drivewayHeadline,
    drivewayPriceClause,
    liabilityCover: settings.liabilityCover,
    abn: settings.abn,
    suburb: settings.suburb,
    state: settings.state,
    postcode: settings.postcode,
    machineMobilisationPerKm: String(settings.machineMobilisationPerKm),
    siteEstablishmentFee: String(settings.siteEstablishmentFee),
    homeBase: siteContent.serviceAreaPhrasing.homeBase,
    region: siteContent.serviceAreaPhrasing.region,
    wider: siteContent.serviceAreaPhrasing.wider,
  }
}

/**
 * One {price:<slug>} token per active catalogue row, e.g. {price:gabion-cube}
 * -> "$600". Inactive rows are omitted, so a stale reference in page copy
 * falls back to the literal token text rather than a wrong number -- an
 * annoyance an editor will notice and fix, not a silent wrong price.
 */
export function buildCataloguePriceTokens(items: CatalogueItem[]): TokenMap {
  const tokens: TokenMap = {}
  for (const item of items) {
    if (item.active) tokens[`price:${item.slug}`] = formatPrice(item.unitPrice)
  }
  return tokens
}

export type HighlightColour = 'chartreuse' | 'green' | 'purple'

type InlineSegment =
  | { type: 'text'; text: string }
  | { type: 'highlight'; text: string; colour?: HighlightColour }
  | { type: 'jargon'; slug: string; label?: string }
  | { type: 'aside'; text: string }

const HIGHLIGHT_AND_JARGON =
  /\*\*((?:[^*\n]|\*(?!\*))+?)\*\*|\[\[jargon:([a-z-]+)(?:\|([^\]]+))?\]\]|\(\(([^)\n]+?)\)\)|\(([^()\n]+?)\)/g

const COLOUR_VALUES: ReadonlySet<HighlightColour> = new Set(['chartreuse', 'green', 'purple'])

function parseHighlightBody(body: string): { text: string; colour?: HighlightColour } {
  const pipe = body.indexOf('|')
  if (pipe === -1) return { text: body }
  const text = body.slice(0, pipe).trim()
  const candidate = body
    .slice(pipe + 1)
    .trim()
    .toLowerCase()
  if (COLOUR_VALUES.has(candidate as HighlightColour)) {
    return { text, colour: candidate as HighlightColour }
  }
  return { text: body }
}

/**
 * Walks through a string and returns an ordered list of segments: literal
 * text, highlight markers and jargon references. Used by RichText and
 * HighlightedText on the server.
 */
export function parseInline(input: string): InlineSegment[] {
  if (!input) return []

  const segments: InlineSegment[] = []
  let cursor = 0

  for (const match of input.matchAll(HIGHLIGHT_AND_JARGON)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push({ type: 'text', text: input.slice(cursor, start) })
    }

    if (match[1] !== undefined) {
      const { text, colour } = parseHighlightBody(match[1])
      segments.push(colour ? { type: 'highlight', text, colour } : { type: 'highlight', text })
    } else if (match[2]) {
      const slug = match[2]
      const label = match[3]
      segments.push(label ? { type: 'jargon', slug, label } : { type: 'jargon', slug })
    } else if (match[4] !== undefined || match[5] !== undefined) {
      // Group 4: explicit ((double-paren)) aside marker (legacy / explicit).
      // Group 5: implicit (single-paren) aside (default for any parenthetical).
      segments.push({ type: 'aside', text: match[4] ?? match[5]! })
    }

    cursor = start + match[0].length
  }

  if (cursor < input.length) {
    segments.push({ type: 'text', text: input.slice(cursor) })
  }

  return segments
}
